import React, { useRef, useCallback } from 'react';
import { Box, InputBase, InputLabel, Typography } from '@mui/material';
import { Control, Controller, FieldError } from 'react-hook-form';
import { IFormTypes } from './Types';

const BOX_COUNT = 6;

interface ReferralCodeInputProps {
  control: Control<IFormTypes, any>;
  error: FieldError | undefined;
  dataTestId?: string;
}

const ReferralCodeInput: React.FC<ReferralCodeInputProps> = ({ control, error, dataTestId = 'referral-code-input' }) => {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const focusBox = useCallback((index: number) => {
    const i = Math.max(0, Math.min(index, BOX_COUNT - 1));
    inputRefs.current[i]?.focus();
  }, []);

  const handleKeyDown = useCallback(
    (index: number, value: string, e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (e.key === 'Backspace' && !value && index > 0) {
        e.preventDefault();
        focusBox(index - 1);
      } else if (e.key === 'ArrowLeft' && index > 0) {
        e.preventDefault();
        focusBox(index - 1);
      } else if (e.key === 'ArrowRight' && index < BOX_COUNT - 1) {
        e.preventDefault();
        focusBox(index + 1);
      }
    },
    [focusBox]
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent, onChange: (value: string) => void) => {
      e.preventDefault();
      const pasted = e.clipboardData
        .getData('text')
        .replace(/[^a-zA-Z0-9]/g, '')
        .toUpperCase()
        .slice(0, BOX_COUNT);
      if (pasted) {
        onChange(pasted);
        focusBox(Math.min(pasted.length, BOX_COUNT - 1));
      }
    },
    [focusBox]
  );

  const handleChange = useCallback(
    (index: number, onChange: (value: string) => void, currentValue: string, newChar: string) => {
      const char = newChar.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(-1);
      const chars = currentValue.split('');
      while (chars.length < BOX_COUNT) chars.push('');
      chars[index] = char;
      const next = chars.join('').slice(0, BOX_COUNT);
      onChange(next);
      if (char && index < BOX_COUNT - 1) focusBox(index + 1);
    },
    [focusBox]
  );

  return (
    <Controller
      control={control}
      name="referredByCode"
      render={({ field: { value, onChange } }) => {
        const str = (value || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
        const chars: string[] = [];
        for (let i = 0; i < BOX_COUNT; i++) chars.push(str[i] || '');

        return (
          <Box sx={{ marginBottom: '16px' }} data-testid={`${dataTestId}-container`}>
            <InputLabel
              sx={{
                fontSize: '18px',
                fontWeight: 500,
                marginBottom: '12px',
                alignSelf: 'flex-start',
                color: '#171717',
              }}
            >
              Referral code (optional)
            </InputLabel>
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                justifyContent: 'center',
                flexWrap: 'wrap',
              }}
            >
              {chars.map((char, index) => (
                <InputBase
                  key={index}
                  inputRef={el => {
                    inputRefs.current[index] = el;
                  }}
                  value={char}
                  autoComplete="off"
                  inputProps={{
                    maxLength: 1,
                    'aria-label': `Referral code character ${index + 1}`,
                    'data-testid': `${dataTestId}-box-${index}`,
                    style: { textAlign: 'center' },
                    name: `referral-char-${index}`,
                    autoComplete: 'off',
                    'data-lpignore': 'true',
                    'data-1p-ignore': 'true',
                    'data-form-type': 'other',
                  }}
                  onKeyDown={e => handleKeyDown(index, char, e)}
                  onPaste={e => handlePaste(e, onChange)}
                  onChange={e => handleChange(index, onChange, value || '', e.target.value)}
                  sx={{
                    width: 44,
                    height: 56,
                    border: '2px solid',
                    borderColor: error ? 'error.main' : 'divider',
                    borderRadius: 2,
                    bgcolor: '#fff',
                    boxShadow: '0 4px 20px rgba(208, 208, 208, 0.25)',
                    fontFamily: 'monospace',
                    fontSize: '1.25rem',
                    fontWeight: 700,
                    letterSpacing: 2,
                    '&.Mui-focused': {
                      borderColor: 'primary.main',
                      boxShadow: '0 0 0 3px rgba(25, 118, 210, 0.15)',
                    },
                    '& .MuiInputBase-input': {
                      textAlign: 'center',
                      color: '#171717',
                    },
                  }}
                />
              ))}
            </Box>
            {error && (
              <Typography
                sx={{
                  fontSize: '15px',
                  color: 'red',
                  marginTop: '8px',
                }}
                data-testid={`${dataTestId}-error`}
              >
                {error.message as string}
              </Typography>
            )}
          </Box>
        );
      }}
    />
  );
};

export default ReferralCodeInput;
